/**
 * Table Sorting Composable
 *
 * Handles sorting logic for table columns with support for:
 * - Dates, numbers, and strings
 * - Special mapped values (ticket status, priority)
 * - Currency formatting
 * - Null value handling
 */

import type {
	TableColumn, TableRow
} from '@/components/table/types'
import { TICKET_PRIORITY_MAPPING, TICKET_STATUS_MAPPING } from '@/constants/ticketing'
import {
	sortDateObjects, sortDates, sortMapped, sortNumbers, sortStrings
} from '@/utils/sortMethods/index'
import type { SortOrder } from '@/utils/sortMethods/types'
import type { Ref } from 'vue'

export function useTableSorting() {
	const sortTable = (sort: {
		column: TableColumn
		activeSortColumnKey: Ref<string>
		sortOrder: Ref<SortOrder>
		tableData: Ref<TableRow[]>
	}) => {
		const {
			column, activeSortColumnKey, sortOrder, tableData
		} = sort
		if (!column) {
			return
		}
		const columnKey = column.key
		const columnSortable = column.sortable

		if (!columnSortable) {
			return
		}
		if (activeSortColumnKey.value === columnKey) {
			sortOrder.value = sortOrder.value === 'ASC' ? 'DESC' : 'ASC'
		} else {
			activeSortColumnKey.value = columnKey
			sortOrder.value = 'ASC'
		}

		tableData.value.sort((a, b) => {
			const valA: string | Date = a[columnKey] as string | Date
			const valB: string | Date = b[columnKey] as string | Date
			// dont explode if null values
			if (!valA && !valB) {
				return 0
			}
			if (!valA) {
				return sortOrder.value === 'ASC' ? -1 : 1
			}
			if (!valB) {
				return sortOrder.value === 'ASC' ? 1 : -1
			}
			if (typeof valA === 'string' && valA.match(/\d{4}-\d{2}-\d{2}/) && typeof valB === 'string' && valB.match(/\d{4}-\d{2}-\d{2}/)) {
				return sortDates(valA, valB, sortOrder.value)
			}

			if (typeof valA === 'object' && typeof valA?.getMonth === 'function' && typeof valB === 'object' && typeof valB?.getMonth === 'function') {
				return sortDateObjects(valA, valB, sortOrder.value)
			}

			// remove dollar signs if present
			const valAString = (typeof valA === 'string' && valA?.replace(/\$/g, '')) || valA?.toString()
			const valBString = (typeof valB === 'string' && valB?.replace(/\$/g, '')) || valB?.toString()

			const valANumber = parseFloat(valAString) || 0
			const valBNumber = parseFloat(valBString) || 0

			const valsAreNaN = () => {
				return isNaN(valANumber) || isNaN(valBNumber)
			}

			const valsAreStrings = () => {
				return !!valAString?.length && !!valBString?.length
			}

			if (columnKey as string === 'all') {
				return 0
			} else if (columnKey as string === 'status') {
				return sortMapped(a.status, b.status, sortOrder.value, TICKET_STATUS_MAPPING)
			} else if (columnKey as string === 'priority') {
				return sortMapped(a.priority, b.priority, sortOrder.value, TICKET_PRIORITY_MAPPING)
			} else if (columnKey as string === 'category') {
				return sortStrings(a.category, b.category, sortOrder.value)
			} else if ((typeof valA === 'string' && typeof valB === 'string') && (columnKey as string === 'lastUpdated' || columnKey as string === 'dueDate' || columnKey as string === 'lastDate')) {
				return sortDates(valA, valB, sortOrder.value)
			} else if (columnKey as string === 'created' || columnKey as string === 'updated') {
				return sortDateObjects(new Date(valA), new Date(valB), sortOrder.value)
			}

			const sortNumberValues = !!(valANumber && valBNumber && !valsAreNaN())

			if (sortNumberValues) {
				return sortNumbers(parseFloat(valAString), parseFloat(valBString), sortOrder.value)
			}
			if (valsAreStrings()) {
				return sortStrings(valAString, valBString, sortOrder.value)
			}
			return 0
		})
	}

	return { sortTable }
}
